export module NationalIdentityNumbers {
	export class Validator {
		constructor() {}

		static validate(countryCode: string, input: string, nullable: boolean): boolean {
			if (
				typeof input === 'undefined' ||
				input === null ||
				input === '' ||
				typeof countryCode === 'undefined' ||
				countryCode === null
			)
				return nullable;

			const validator = Validator[countryCode.toUpperCase()];
			if (typeof validator !== 'function') return false;

			return validator.valid(input);
		}

		static supportsCountry(countryCode: string): boolean {
			const validator = Validator[countryCode.toUpperCase()];
			return typeof validator === 'function';
		}
	}

	// Implementations
	export module Validator {
		export class SE {
			// https://en.wikipedia.org/wiki/National_identification_number#Sweden
			static valid(input: string): boolean {
				var REGEXP = /^([0-9]{6}|[0-9]{8})[-|+]?[0-9A-Z]{4}$/i;
				if (input.length < 10 || input.length > 13 || !REGEXP.test(input)) return false;

				// Normalize to YYYYMMDDXXXZ
				let normalized = '';
				switch (input.length) {
					case 11: // YYMMDD?XXXZ
						var centuryMod = input[6] === '+' ? 1 : 0;
						normalized =
							Math.floor((new Date().getFullYear() - parseInt(input.substring(0, 2))) / 100) +
							centuryMod +
							input.substring(0, 6) +
							input.substring(7);
						break;

					case 10: // YYMMDDXXXZ (century guessing; by the year you were born, not the birthdate)
						normalized =
							Math.floor((new Date().getFullYear() - parseInt(input.substring(0, 2))) / 100) +
							input.substring(0, 6) +
							input.substring(6);
						break;

					case 12: // YYYYMMDDXXXZ
						normalized = input;
						break;

					case 13: // YYYYMMDD?XXXZ
						normalized = input.substring(0, 8) + input.substring(9);
						break;
				}

				// Apply checksum if there are no letters (expected behavior with letters is unknown)
				if (normalized.match(/[A-Z]/gi) === null && !Algorithms.luhnCheck(normalized.substring(2))) return false;

				return true;
			}
		}

		export class NO {
			// http://www.skatteetaten.no/en/Person/National-Registry/Birth-and-name-selection/Children-born-in-Norway/National-ID-number/
			static valid(input: string): boolean {
				var REGEXP = /^[0-9]{11}$/i;
				if (!REGEXP.test(input)) return false;

				// No normalization
				var normalized = input;

				// Validate check digits
				if (!Algorithms.validateNorwegianBirthNumber(normalized)) return false;

				return true;
			}
		}

		export class FI {
			// https://en.wikipedia.org/wiki/National_identification_number#Finland

			static valid(input: string): boolean {
				let checksumChar = '0123456789ABCDEFHJKLMNPRSTUVWXY';
				let normalized = input.toUpperCase();

				var REGEXP = /^[0-9]{6}[-|+|A][0-9]{3}[0-9A-Z]$/i;
				if (!REGEXP.test(normalized)) return false;

				// Validate checksum character

				var mod = parseInt(normalized.substring(0, 6) + normalized.substring(7, 10)) % 31;
				if (normalized[normalized.length - 1] !== checksumChar[mod]) return false;

				return true;
			}
		}

		export class DK {
			private static Format: RegExp = new RegExp('^\\d{6}-(\\d{4}|[a-z|æ|ø|å]{4})$', 'i');
			private static TempCPRLastChars: RegExp = new RegExp('^[a-z|æ|ø|å]{4}$', 'i');
			private static IsTempCPR(input: string): boolean {
				return DK.TempCPRLastChars.test(input.substring(7, 11));
			}
			private static isBirthDateValid(input: string, century: string): boolean {
				let birthDay: Date;
				let dateString: string = century + input.substring(4, 6) + '/' + input.substring(2, 4) + '/' + input.substring(0, 2);
				birthDay = new Date(dateString);
				if (birthDay.toString() === 'Invalid Date' || parseInt(input.substring(2, 4)) !== birthDay.getMonth() + 1) {
					return false;
				}
				return true;
			}
			static valid(input: string): boolean {
				let match = DK.Format.exec(input);
				if (match === null || match.length == 0) return false;
				if (this.IsTempCPR(input)) return DK.isBirthDateValid(input, '19');

				let birthCentury = Number(input[7]) > 4 ? '19' : '20';
				return DK.isBirthDateValid(input, birthCentury);
			}
		}

		export class GB {
			// https://www.nhs.uk/using-the-nhs/about-the-nhs/what-is-an-nhs-number/
			// https://en.wikipedia.org/wiki/NHS_number

			private static TrimWsRegex: RegExp = /\s+/g;
			private static ValidationRegex: RegExp = /^[0-9]{10}$/;

			static valid(input: string): boolean {
				// Strip all whitespace.
				const normalizedNumber = input.replace(GB.TrimWsRegex, '');

				// Verify that the NHS number consists of exactly 10 ASCII digits.
				if (!GB.ValidationRegex.test(normalizedNumber))
				{
					return false;
				}

				let checksum = 0;

				// Multiply each of the first 9 digits by 11 subtracted by their 1-based position (here,
				// it is 10 subtracted by their 0-based position for simplicity).
				for (let i = 0; i < 9; i++)
				{
					checksum += +normalizedNumber[i] * (10 - i);
				}

				// Get the remainder of the checksum divided by 11, then subtract 11 by the remainder to
				// get the checksum total.  If the total is 11, it is considered to be 0 (done here by
				// getting the remainder of the total divided by 11).
				checksum = (11 - (checksum % 11)) % 11;

				// If the total is equal to the check digit, the NHS number is valid.
				return checksum === +normalizedNumber[9];
			}
		}
	}

	class Algorithms {
		/**
		 * Luhn algorithm in JavaScript: validate credit card number supplied as string of numbers
		 * @author ShirtlessKirk. Copyright (c) 2012.
		 * @license WTFPL (http://www.wtfpl.net/txt/copying)
		 */
		static luhnCheck(input: string): boolean {
			const arr = [0, 2, 4, 6, 8, 1, 3, 5, 7, 9];
			let len = input.length,
				bit = 1,
				sum = 0,
				val: number;

			while (len) {
				val = parseInt(input.charAt(--len), 10);
				sum += (bit ^= 1) ? arr[val] : val;
			}

			return sum && sum % 10 === 0;
		}

		/// <summary>
		/// Validates a norwegian Birth Number by verifying its check digits.
		/// </summary>
		static validateNorwegianBirthNumber(birthNumber: any): boolean {
			// https://en.wikipedia.org/wiki/National_identification_number#Norway (doesn't contain detailed info)
			// https://github.com/miles-no/no-validation-scala/blob/master/src/main/scala/no/sand/eirik/novalidation/ValidBirthNumber.scala (implementation from here)

			// Each check digit is calculated by multiplying every digit to the left of it with
			// a specific factor per index, summing the result and then doing 11 - sum % 11.
			// If the calculated digits match those in the input number, the number is valid.

			var digit1 = this.validateNorwegianBirthNumber_Checksum(birthNumber, [3, 7, 6, 1, 8, 9, 4, 5, 2]);
			var digit2 = this.validateNorwegianBirthNumber_Checksum(birthNumber, [5, 4, 3, 2, 7, 6, 5, 4, 3, 2]);

			return parseInt(birthNumber[9]) === digit1 && parseInt(birthNumber[10]) === digit2;
		}

		private static validateNorwegianBirthNumber_Checksum(birthNumber: any, factors: Array<number>) {
			let sum = 0;
			let cc0 = '0'.charAt(0) as any;
			for (var n = 0; n < factors.length; n++) sum += factors[n] * (birthNumber.charAt(n) - cc0);

			let result = 11 - (sum % 11);
			return result === 11 ? 0 : result;
		}
	}
}
